import { CameraOff, Download, TriangleAlert, Volume2 } from "lucide-react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useRoomController, useRoomState } from "../../room/RoomContext";
import { describeNoticeToasts, type NoticeToastSpec } from "../../room/notice-toasts";
import { StageToaster, ToastContent } from "../ui/sonner";

const icons = {
  "audio-unlock": <Volume2 />,
  "camera-status": <CameraOff />,
  "screen-audio-warning": <TriangleAlert />,
  "update-notice": <Download />,
} as const;

function showToast(spec: NoticeToastSpec, actions: { action: () => void; cancel: () => void }) {
  const run = (command: () => unknown) => () => {
    void command();
    toast.dismiss(spec.id);
  };
  toast.custom(
    () => (
      <ToastContent
        icon={icons[spec.id as keyof typeof icons]}
        tone={spec.tone}
        title={spec.title}
        description={spec.description}
        action={
          spec.actionLabel ? { label: spec.actionLabel, onClick: run(actions.action) } : undefined
        }
        cancel={
          spec.cancelLabel ? { label: spec.cancelLabel, onClick: run(actions.cancel) } : undefined
        }
      />
    ),
    {
      id: spec.id,
      duration: Infinity,
      position: spec.top ? "top-center" : "bottom-center",
    },
  );
}

// Drives Sonner toasts from store state. The controller only patches the
// store; this component is the single place that calls toast().
export function StageToasts() {
  const { t, i18n } = useTranslation();
  const controller = useRoomController();
  const audioUnlockVisible = useRoomState((s) => s.notices.audioUnlockVisible);
  const cameraStatus = useRoomState((s) => s.notices.cameraStatus);
  const screenAudioWarningVisible = useRoomState((s) => s.notices.screenAudioWarningVisible);
  const updateNoticeVisible = useRoomState((s) => s.notices.updateNoticeVisible);
  const updateNoticeReason = useRoomState((s) => s.notices.updateNoticeReason);
  const updateCanApply = useRoomState((s) => s.notices.updateCanApply);
  const shown = useRef<Set<string>>(new Set());

  useEffect(() => {
    const specs = describeNoticeToasts(
      {
        audioUnlockVisible,
        cameraStatus,
        screenAudioWarningVisible,
        updateNoticeVisible,
        updateNoticeReason,
        updateCanApply,
      },
      {
        audioUnlockTitle: t("voice.enableRoomAudio"),
        audioUnlockAction: t("voice.enableRoomAudio"),
        cameraTitle: t("common.camera"),
        screenAudioTitle: t("screen.ownAudioWarningTitle"),
        screenAudioDescription: t("screen.ownAudioWarningDescription"),
        screenAudioDismiss: t("screen.dismissOwnAudioWarning"),
        dismiss: t("common.close"),
        updateTitle: t("updates.available"),
        updateReason: t(`updates.${updateNoticeReason}`),
        updateAction: t("updates.apply"),
      },
    );
    const active = new Set(specs.map((spec) => spec.id));
    for (const spec of specs) {
      const run = (command: () => unknown) => () => void command();
      showToast(spec, {
        action:
          spec.id === "audio-unlock"
            ? run(controller.enableRoomAudio)
            : run(() => window.location.reload()),
        cancel:
          spec.id === "camera-status"
            ? run(controller.dismissCameraStatus)
            : spec.id === "screen-audio-warning"
              ? run(controller.dismissScreenAudioWarning)
              : run(controller.dismissUpdateNotice),
      });
    }
    for (const id of shown.current) {
      if (!active.has(id)) toast.dismiss(id);
    }
    shown.current = active;
    // i18n.language re-fires the visible toasts with translated text.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t is intentionally omitted: i18n.language already re-fires on language change, while t would re-fire toasts on unrelated renders
  }, [
    audioUnlockVisible,
    cameraStatus,
    screenAudioWarningVisible,
    updateNoticeVisible,
    updateNoticeReason,
    updateCanApply,
    i18n.language,
    controller,
  ]);

  useEffect(() => () => void toast.dismiss(), []);
  return <StageToaster />;
}
